/** @fileoverview get_user_analytics_aggregated_data の共通定数 */

/** ツール名（単一参照点） */
export const GET_USER_ANALYTICS_AGGREGATED_DATA_NAME =
  "get_user_analytics_aggregated_data" as const;

/** LLM へ提示する説明文 */
export const GET_USER_ANALYTICS_AGGREGATED_DATA_DESC = `Returns GA4-derived session performance aggregates for a User Analytics project or a saved view.

Use this when the user asks for UA metrics such as sessions, conversions, engagement, surf rate, or needs breakdowns by first/second page URL groups, traffic source, or default channel group.

Important:
- If the user's question clearly indicates which metrics are needed, include 'metrics' to minimize tokens (pick only what you need).
  If unclear, omit 'metrics' to keep results broad.
  Options: SESSION_COUNT | UNIQUE_USER_COUNT | CONVERSION_RATE | CONVERSION_COUNT | SURF_RATE | SURF_COUNT | AVG_SESSION_DURATION | ENGAGEMENT_RATE | ENGAGEMENT_COUNT | PAGE_VIEW_EVENT_COUNT
  If you request a *_RATE, also include SESSION_COUNT + the corresponding *_COUNT.
- If your request needs session-level diagnostics (e.g., "why did avg session duration change?"), prefer get_user_analytics_raw_data.

Inputs (key fields):
- 'projectId' (required): User Analytics project ID. Optionally add 'viewId' to reuse a saved view's filters.
- 'dateRange' (required): { start, end } ISO timestamps.
- Optional 'metrics': which metrics to include in output rows/summary. If omitted, returns the full UA dashboard fragment (backward-compatible).
- Optional 'movingAverage': none | 7d | 28d | both. Defaults to 'none'. Controls which moving average fields are returned (e.g., sc7d, cr7d, sc28d).
- Optional filters: 'groupBy', page locations + URL group IDs, OS/device/browser enums, source/medium/campaign/Term/Content filters (with campaign filter operators), events to include/exclude, eventParams ({ key, operator, value }[]), experiment variants, default channel groups, navigation-only sessions flag, page location count range, GA session number range, visited page filters, and user/session ID filters.
- For period comparisons, pass both 'isComparing' and 'comparingDateRange'. If one is omitted, the tool auto-corrects it and still runs a single comparison call.
- If 'comparingDateRange' is omitted in comparison mode, it defaults to 'dateRange'.

Output:
- Metadata describing the applied filters and grouping.
- Arrays for aggregated, grouped, comparing aggregated, and comparing grouped session performance (matching the UA dashboard fragment).

Notes:
- Read-only; safe to run repeatedly.
- Mirrors the UI's GetAggregatedData behavior so metrics align with the dashboard.
- IMPORTANT: Aggregated data is for trends/summary only. Do NOT infer exact conversion/purchase counts or their drivers from aggregated outputs.
- IMPORTANT: Aggregated AVG_SESSION_DURATION is trend-oriented. Use get_user_analytics_raw_data for root-cause diagnostics.
- If the user asks “Did purchases go up/down vs last month? Why?” (e.g., GA4 purchase change for a specific project), you MUST call get_user_analytics_raw_data (and, if needed, list_sessions) with appropriate filters/groupBy and base the answer on raw results.
`;
